// Main-thread engine host. RTCPeerConnection is a Window-only API — it is not
// exposed in Web Workers in Chrome/Firefox/Safari — so the signalling socket,
// the sequential run/sweep drivers and the peer connections all run here on the
// main thread. Probe traffic is light (tens to a few hundred pps), so this
// keeps the UI responsive without a worker while staying compatible with real
// browsers.
//
// The host never probes two nodes at once: runMulti and runSweep are strictly
// sequential (constraint 1). This module exposes the same command/event shapes
// the store already speaks, so it is a drop-in for the previous worker.

import { NodeConnection } from './connection';
import { type EngineDeps, defaultRandomRunId, runMulti, runNode } from './engine';
import { localize } from './metrics';
import { Pacer } from './pacer';
import { SignalingClient } from './signaling';
import { runSweep, type SweepDeps, type SweepRunner } from './sweep';
import {
  type CellResult,
  type NodeInfo,
  type RunResult,
  type Settings,
  type SweepSpec,
} from './types';

/** Signalling connection parameters. */
export interface SignalingConfig {
  url: string;
  token?: string;
}

/** Commands the UI posts into the engine host. */
export type WorkerCommand =
  | {
      type: 'run';
      config: SignalingConfig;
      nodes: NodeInfo[];
      settings: Settings;
      iceServers?: RTCIceServer[];
      sessionInfo?: { clientIp?: string; asn?: number; asnOrg?: string };
      traceroute?: boolean;
    }
  | {
      type: 'sweep';
      config: SignalingConfig;
      spec: SweepSpec;
      nodeInfos: NodeInfo[];
      iceServers?: RTCIceServer[];
      sessionInfo?: { clientIp?: string; asn?: number; asnOrg?: string };
    }
  | { type: 'cancel' };

/** Events the engine host emits back to the UI. */
export type WorkerEvent =
  | { type: 'ready' }
  | { type: 'nodes'; nodes: NodeInfo[] }
  | { type: 'nodeStart'; nodeId: string }
  | {
      type: 'progress';
      nodeId: string;
      sent: number;
      echoed: number;
      total: number;
      elapsedMs: number;
    }
  | { type: 'runResult'; result: RunResult }
  | { type: 'runsDone'; results: RunResult[]; verdict: ReturnType<typeof localize> }
  | {
      type: 'sweepCellStart';
      nodeId: string;
      size: number;
      pps: number;
      repeat: number;
      index: number;
      total: number;
    }
  | { type: 'sweepCell'; cell: CellResult }
  | { type: 'sweepDone'; result: import('./types').SweepResult }
  | { type: 'signalError'; nodeId?: string; error: string }
  | { type: 'error'; message: string }
  | { type: 'cancelled' };

/** A running engine host; feed it commands with handle(). */
export interface EngineHost {
  handle(cmd: WorkerCommand): void;
}

/**
 * Create a main-thread engine host. `post` receives every event the host emits
 * (wire it to the store's applyWorkerEvent). Returns an object whose handle()
 * accepts the same commands the old worker did.
 */
export function createEngineHost(post: (msg: WorkerEvent) => void): EngineHost {
  // Shared cancellation signal for the in-flight job.
  const abortState = { aborted: false };

  /** Promise resolving after `ms`; resolves immediately when cancelled. */
  function delay(ms: number, signal?: { aborted: boolean }): Promise<void> {
    return new Promise<void>((resolve) => {
      if (signal?.aborted) {
        resolve();
        return;
      }
      setTimeout(resolve, ms);
    });
  }

  /** Build engine dependencies backed by real browser primitives. */
  function makeEngineDeps(
    signaling: SignalingClient,
    iceServers: RTCIceServer[] | undefined,
    sessionInfo?: { clientIp?: string; asn?: number; asnOrg?: string },
    traceroute?: boolean,
  ): EngineDeps {
    return {
      signaling,
      sessionInfo,
      traceroute,
      createConnection: (args) =>
        new NodeConnection({
          nodeId: args.nodeId,
          runId: args.runId,
          signaling: args.signaling,
          onFrame: args.onFrame,
          iceServers,
          ipMode: args.ipMode,
        }),
      makePacer: (pps) => new Pacer(pps),
      now: () => performance.now() * 1000,
      delay,
      randomRunId: defaultRandomRunId,
    };
  }

  /** Connect a signalling client and forward node lists / errors to the page. */
  async function connectSignaling(config: SignalingConfig): Promise<SignalingClient> {
    const signaling = new SignalingClient({ url: config.url, token: config.token });
    signaling.on('nodes', (m) => post({ type: 'nodes', nodes: m.nodes }));
    signaling.on('error', (m) => post({ type: 'signalError', nodeId: m.nodeId, error: m.error }));
    await signaling.connect();
    signaling.hello();
    return signaling;
  }

  /** Drive a multi-node run and report results. */
  async function handleRun(cmd: Extract<WorkerCommand, { type: 'run' }>): Promise<void> {
    const signaling = await connectSignaling(cmd.config);
    const deps = makeEngineDeps(signaling, cmd.iceServers, cmd.sessionInfo, cmd.traceroute);
    try {
      const results = await runMulti(
        cmd.nodes,
        cmd.settings,
        deps,
        {
          onNodeStart: (nodeId) => post({ type: 'nodeStart', nodeId }),
          onProgress: (p) => post({ type: 'progress', ...p }),
          onNodeDone: (result) => post({ type: 'runResult', result }),
        },
        abortState,
      );
      if (abortState.aborted) {
        post({ type: 'cancelled' });
      } else {
        post({ type: 'runsDone', results, verdict: localize(results) });
      }
    } finally {
      signaling.close();
    }
  }

  /** Drive a sequential sweep and report results. */
  async function handleSweep(cmd: Extract<WorkerCommand, { type: 'sweep' }>): Promise<void> {
    const signaling = await connectSignaling(cmd.config);
    const deps = makeEngineDeps(signaling, cmd.iceServers, cmd.sessionInfo);
    const infoById = new Map<string, NodeInfo>();
    for (const info of cmd.nodeInfos) infoById.set(info.id, info);

    const runner: SweepRunner = {
      runCell: async ({ node, settings }) => {
        const result = await runNode(node, settings, deps, {}, abortState);
        return result;
      },
      // runNode already closes its own connection each cell, so there is nothing
      // extra to tear down at a node boundary.
      closeNode: () => {},
    };

    const sweepDeps: SweepDeps = {
      runner,
      delay,
      nodeInfo: (id) => {
        const info = infoById.get(id);
        if (!info) throw new Error(`sweep: unknown node ${id}`);
        return info;
      },
    };

    try {
      const result = await runSweep(
        cmd.spec,
        sweepDeps,
        {
          onCellStart: (c) =>
            post({
              type: 'sweepCellStart',
              nodeId: c.nodeId,
              size: c.size,
              pps: c.pps,
              repeat: c.repeat,
              index: c.index,
              total: c.total,
            }),
          onCell: (cell) => post({ type: 'sweepCell', cell }),
        },
        abortState,
      );
      if (abortState.aborted) {
        post({ type: 'cancelled' });
      } else {
        post({ type: 'sweepDone', result });
      }
    } finally {
      signaling.close();
    }
  }

  function handle(cmd: WorkerCommand): void {
    switch (cmd.type) {
      case 'cancel':
        abortState.aborted = true;
        return;
      case 'run':
        abortState.aborted = false;
        handleRun(cmd).catch((err) =>
          post({ type: 'error', message: err instanceof Error ? err.message : String(err) }),
        );
        return;
      case 'sweep':
        abortState.aborted = false;
        handleSweep(cmd).catch((err) =>
          post({ type: 'error', message: err instanceof Error ? err.message : String(err) }),
        );
        return;
      default:
        return;
    }
  }

  post({ type: 'ready' });
  return { handle };
}
